import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { apiError, catColor } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useCategories } from '../context/CategoriesContext';
import { LabelDesigner, DEFAULT_TEMPLATE, normTemplate } from '../components/LabelDesign';
import { BillDesigner, normBill } from '../components/BillDesign';

const TABS = ['Business', 'Bill / Invoice', 'Labels & Barcodes', 'Product Categories'];

// Owner tool: review + remove weight-only "pack" variants (import artifacts) so
// loose products (sweets) show as a single loose line. Any stock on a pack is
// moved back to loose before the variant is removed.
const LooseCleanupModal = ({ onClose }) => {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/products/loose-cleanup/preview').then(({ data }) => {
    setRows(data.candidates || []);
    setSel(Object.fromEntries((data.candidates || []).map((c) => [c.productId, true])));
  }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const list = rows || [];
  const selected = list.filter((c) => sel[c.productId]);
  const allOn = list.length > 0 && selected.length === list.length;
  const toggleAll = () => setSel(allOn ? {} : Object.fromEntries(list.map((c) => [c.productId, true])));
  const totalPacks = selected.reduce((n, c) => n + c.variants.length, 0);

  const remove = async () => {
    if (!selected.length) return toast.error('Select at least one product');
    if (!window.confirm(`Remove ${totalPacks} pack variant(s) from ${selected.length} product(s)? Any stock on them is kept as by-weight stock. This cannot be undone.`)) return;
    if (window.prompt('Type CLEAN to confirm.') !== 'CLEAN') return;
    setBusy(true);
    try { const { data } = await api.post('/products/loose-cleanup', { productIds: selected.map((c) => c.productId) }); toast.success(data.message || 'Cleaned up'); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Clean up phantom pack variants" onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Close</button>
        <button className="btn btn-danger" onClick={remove} disabled={busy || !selected.length}>{busy ? 'Removing…' : `Remove ${totalPacks || 0} pack${totalPacks === 1 ? '' : 's'}`}</button></>}>
      {rows === null ? <div className="spinner" /> : !list.length ? <p className="muted">No weight-only pack variants found — nothing to clean.</p> : (<>
        <p className="muted small" style={{ marginTop: 0 }}>These products carry weight-only “pack” variants (e.g. 1KG) — usually import artifacts on by-weight products like sweets. Removing them leaves the product by-weight only; any stock on a pack is kept as by-weight stock, and the product’s measure unit is set from the pack (1KG → KGS, 500ML → LTR) if it’s still the generic “unit”. <strong>Untick anything that’s a real pack you want to keep.</strong></p>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr>
              <th style={{ width: 32 }}><input type="checkbox" checked={allOn} onChange={toggleAll} title="Select all" /></th>
              <th>Product</th><th>Category</th><th>Pack variants</th><th>Unit</th><th className="right">On hand</th>
            </tr></thead>
            <tbody>
              {list.map((c) => {
                const stock = c.variants.reduce((n, v) => n + Number(v.onHand || 0), 0);
                return (
                  <tr key={c.productId}>
                    <td><input type="checkbox" checked={!!sel[c.productId]} onChange={(e) => setSel((s) => ({ ...s, [c.productId]: e.target.checked }))} /></td>
                    <td>{c.name}{!c.allPhantom && <span className="badge badge-gold" style={{ marginLeft: 6 }} title="Also has non-weight packs — review carefully">mixed</span>}</td>
                    <td>{c.category ? <span className="cat-chip" style={{ '--cat': catColor(c.category) }}>{c.category}</span> : '—'}</td>
                    <td>{c.variants.map((v) => v.weightLabel).join(', ')}</td>
                    <td>{c.setUnit ? <span className="small">{c.currentUnit || 'unit'} → <strong>{c.setUnit}</strong></span> : <span className="muted small">{c.currentUnit || c.unit}</span>}</td>
                    <td className="right">{stock > 0 ? <span className="badge badge-gold" title="Kept as by-weight stock">{stock}</span> : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>)}
    </Modal>
  );
};

const Settings = () => {
  const { can } = useAuth();
  const editable = can('settings:write');
  const { categories, setCategories } = useCategories();
  const [tab, setTab] = useState(TABS[0]);
  const [settings, setSettings] = useState({});
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [labelDraft, setLabelDraft] = useState(DEFAULT_TEMPLATE);
  const [labelDirty, setLabelDirty] = useState(false);
  const [billDraft, setBillDraft] = useState(normBill(null));
  const [billDirty, setBillDirty] = useState(false);
  const [cats, setCats] = useState([]);
  const [catsDirty, setCatsDirty] = useState(false);
  const [newCat, setNewCat] = useState('');

  useEffect(() => { setCats(categories); }, [categories]);

  useEffect(() => {
    Promise.all([
      api.get('/settings').then(({ data }) => {
        setSettings(data.settings || {});
        setLabelDraft(normTemplate(data.settings?.labelTemplate));
        setBillDraft(normBill(data.settings?.billTemplate));
      }),
      api.get('/locations').then(({ data }) => setLocations(data.locations || [])),
    ]).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  const addCat = () => {
    const v = newCat.trim();
    if (!v || cats.some((c) => c.toLowerCase() === v.toLowerCase())) { setNewCat(''); return; }
    setCats((p) => [...p, v]); setNewCat(''); setCatsDirty(true);
  };
  const rmCat = (c) => { setCats((p) => p.filter((x) => x !== c)); setCatsDirty(true); };
  const moveCat = (i, dir) => setCats((p) => { const n = [...p]; const j = i + dir; if (j < 0 || j >= n.length) return p; [n[i], n[j]] = [n[j], n[i]]; setCatsDirty(true); return n; });
  const saveCats = async () => {
    try {
      const { data } = await api.put('/products/categories', { categories: cats });
      const saved = data.categories || cats;
      setCats(saved); setCategories(saved); setCatsDirty(false); toast.success('Categories saved');
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async (key, value) => {
    try { await api.put('/settings', { key, value }); setSettings((p) => ({ ...p, [key]: value })); toast.success('Saved'); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveLabel = async () => {
    try { await api.put('/settings', { key: 'labelTemplate', value: labelDraft }); setSettings((p) => ({ ...p, labelTemplate: labelDraft })); setLabelDirty(false); toast.success('Label design saved'); }
    catch (e) { toast.error(apiError(e)); }
  };
  const changeLabel = (next) => { setLabelDraft(next); setLabelDirty(true); };

  const saveBill = async () => {
    try { await api.put('/settings', { key: 'billTemplate', value: billDraft }); setSettings((p2) => ({ ...p2, billTemplate: billDraft })); setBillDirty(false); toast.success('Bill design saved'); }
    catch (e) { toast.error(apiError(e)); }
  };
  const changeBill = (next) => { setBillDraft(next); setBillDirty(true); };

  const [looseCleanup, setLooseCleanup] = useState(false);
  const [clearing, setClearing] = useState(false);
  const clearTransactions = async () => {
    if (!window.confirm('This permanently deletes ALL stock transfers, requests, returns, production batches, kitchen orders and procurement orders, then rebuilds on-hand stock. It cannot be undone. Continue?')) return;
    if (window.prompt('Type CLEAR to confirm.') !== 'CLEAR') return;
    setClearing(true);
    try { const { data } = await api.post('/inventory/clear-transactions'); toast.success(data.message || 'Transaction data cleared'); }
    catch (e) { toast.error(apiError(e)); } finally { setClearing(false); }
  };

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div className="tabs">
        {TABS.map((t) => <button key={t} className={`tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>{t}</button>)}
      </div>

      {tab === 'Business' && (
        <div className="card card-pad" style={{ maxWidth: 560 }}>
          <div className="field">
            <label>Online fulfillment location (deducts stock for website/webhook orders)</label>
            <select disabled={!editable} value={settings.online_fulfillment_location_id || ''} onChange={(e) => save('online_fulfillment_location_id', Number(e.target.value))}>
              <option value="">— none —</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.type})</option>)}
            </select>
          </div>
          <div className="row">
            <div className="field"><label>Bill number prefix</label><input defaultValue={settings.pos_prefix || 'POS'} disabled={!editable} onBlur={(e) => editable && save('pos_prefix', e.target.value)} /></div>
            <div className="field"><label>Invoice prefix</label><input defaultValue={settings.invoice_prefix || 'INV'} disabled={!editable} onBlur={(e) => editable && save('invoice_prefix', e.target.value)} /></div>
          </div>
          <div className="mt">
            <label style={{ fontSize: '.8rem', color: 'var(--muted)', fontWeight: 600 }}>Storefront webhook</label>
            <p className="muted" style={{ fontSize: '.84rem' }}>Point the storefront's <span className="mono">OPS_WEBHOOK_URL</span> at <span className="mono">/api/webhooks/order-shipped</span> and set the shared <span className="mono">OPS_WEBHOOK_SECRET</span> to match Nexus's env. Shipped online orders then deduct stock from the fulfillment location above.</p>
          </div>
        </div>
      )}

      {tab === 'Business' && can('*') && (
        <div className="card card-pad" style={{ maxWidth: 560, marginTop: '1rem', border: '1px solid var(--danger, #c0392b)' }}>
          <div className="section-head" style={{ marginTop: 0 }}><h3 style={{ margin: 0, color: 'var(--danger, #c0392b)' }}>Danger zone — owner only</h3></div>
          <p className="muted small" style={{ marginTop: 0 }}>Clear all transaction data — stock transfers, requests, returns, production (making), kitchen orders, procurement and packing — then rebuild on-hand stock. Products, recipes, sales, employees and finance are kept. <strong>This cannot be undone.</strong></p>
          <button className="btn btn-danger" onClick={clearTransactions} disabled={clearing}>{clearing ? 'Clearing…' : 'Clear transaction data'}</button>
          <hr style={{ border: 'none', borderTop: '1px solid var(--line)', margin: '1rem 0' }} />
          <p className="muted small" style={{ marginTop: 0 }}>Remove phantom weight-only “pack” variants (e.g. 1KG) that CSV import added to by-weight products, so each shows as a single line. Review the list first; any stock on a pack is kept as by-weight stock. <strong>This cannot be undone.</strong></p>
          <button className="btn btn-danger" onClick={() => setLooseCleanup(true)}>Clean up pack variants…</button>
        </div>
      )}

      {looseCleanup && <LooseCleanupModal onClose={() => setLooseCleanup(false)} />}

      {tab === 'Bill / Invoice' && (
        <div className="card card-pad" style={{ maxWidth: 900 }}>
          <div className="section-head" style={{ marginTop: 0 }}>
            <h3 style={{ margin: 0 }}>Bill design</h3>
            {editable && <button className="btn btn-primary btn-sm" onClick={saveBill} disabled={!billDirty}>{billDirty ? 'Save bill design' : 'Saved'}</button>}
          </div>
          <p className="muted small" style={{ marginTop: 0 }}>Paper, header and what appears on a printed bill. Billing and Invoices print from this design — a thermal roll and an A4 sheet each get a page of their own size, with nothing inherited from the app.</p>
          <BillDesigner value={billDraft} onChange={changeBill} disabled={!editable} />
        </div>
      )}

      {tab === 'Labels & Barcodes' && (
        <div className="card card-pad" style={{ maxWidth: 820 }}>
          <div className="section-head" style={{ marginTop: 0 }}>
            <h3 style={{ margin: 0 }}>Barcode label design</h3>
            {editable && <button className="btn btn-primary btn-sm" onClick={saveLabel} disabled={!labelDirty}>{labelDirty ? 'Save label design' : 'Saved'}</button>}
          </div>
          <p className="muted small" style={{ marginTop: 0 }}>Set the size, layout and contents of your barcode labels once here. The Print Labels screen uses this finalized design — staff only pick the manufacturing date and how many to print.</p>
          <LabelDesigner value={labelDraft} onChange={changeLabel} disabled={!editable} />
        </div>
      )}

      {tab === 'Product Categories' && (
        <div className="card card-pad" style={{ maxWidth: 560 }}>
          <div className="section-head" style={{ marginTop: 0 }}>
            <h3 style={{ margin: 0 }}>Product categories</h3>
            {editable && <button className="btn btn-primary btn-sm" onClick={saveCats} disabled={!catsDirty}>{catsDirty ? 'Save categories' : 'Saved'}</button>}
          </div>
          <p className="muted small" style={{ marginTop: 0 }}>These are the categories offered when adding or editing a product, everywhere in the app. Existing products keep their category even if you remove it here.</p>
          {editable && (
            <div className="flex gap" style={{ marginBottom: '.7rem', alignItems: 'center' }}>
              <input placeholder="New category…" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCat(); } }} style={{ maxWidth: 280 }} />
              <button className="btn btn-outline btn-sm" onClick={addCat} disabled={!newCat.trim()}>+ Add</button>
            </div>
          )}
          <table className="grid">
            <tbody>
              {cats.map((c, i) => (
                <tr key={c}>
                  <td><span className="cat-chip" style={{ '--cat': catColor(c) }}>{c}</span></td>
                  {editable && (
                    <td className="right" style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => moveCat(i, -1)} disabled={i === 0} title="Move up">↑</button>{' '}
                      <button className="btn btn-outline btn-sm" onClick={() => moveCat(i, 1)} disabled={i === cats.length - 1} title="Move down">↓</button>{' '}
                      <button className="btn btn-danger btn-sm" onClick={() => rmCat(c)} title="Remove">✕</button>
                    </td>
                  )}
                </tr>
              ))}
              {!cats.length && <tr><td className="empty">No categories yet — add one above.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Settings;
